import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { Badge, BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  Calendar,
  User,
  FileText,
  DollarSign,
  Tag,
  CreditCard,
  Copy,
  ExternalLink
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu'
import { VoucherResponse } from '@/app/types/voucher'
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header'
import {
  getSimplifiedSalesInvoiceStatus,
  SimplifiedSalesInvoiceStatus
} from '@/app/lib/statusUtils'
// Custom events for row actions
export const editSalesInvoiceEvent = 'editSalesInvoice'
export const deleteSalesInvoiceEvent = 'deleteSalesInvoice'
export const markAsPaidSalesInvoiceEvent = 'markAsPaidSalesInvoice'
export const duplicateSalesInvoiceEvent = 'duplicateSalesInvoice'

// Helper function to format currency
const formatCurrency = (amount: string | number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(Number(amount))
}

// Helper function to format date
const formatDate = (dateString: string): string => {
  try {
    return format(new Date(dateString), 'dd/MM/yyyy')
  } catch (error) {
    return dateString
  }
}

// Helper to get badge variant based on simplified status
const getStatusBadgeVariant = (status: SimplifiedSalesInvoiceStatus): BadgeProps['variant'] => {
  switch (status) {
    case 'Paid':
      return 'secondary'
    case 'Partially Paid':
      return 'secondary'
    case 'Unpaid':
      return 'outline'
    case 'Cancelled':
      return 'destructive'
    default:
      return 'default'
  }
}


// Define extended VoucherResponse interface with optional status fields
interface ExtendedVoucherResponse extends VoucherResponse {
  status?: string
  payment_status?: string
}

// Define custom meta type directly (no module augmentation needed here)
interface CustomColumnMeta {
  label?: string
  placeholder?: string
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange'
  icon?: React.ComponentType<{ className?: string }>
  options?: Array<{ label: string; value: string | number | boolean }>
}

// Use intersection type for ExtendedColumnDef
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta
}

export const columns: ExtendedColumnDef<ExtendedVoucherResponse>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
        disabled={row.original.is_cancelled} // Disable selection for cancelled invoices
      />
    ),
    enableSorting: false,
    enableHiding: false,
    size: 40,
  },
  {
    accessorKey: 'voucher_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Invoice Number" />,
    cell: ({ row }): JSX.Element => {
      const invoiceId = row.original.id
      const invoiceNumber = row.getValue('voucher_number') as string

      const handleClick = (e: React.MouseEvent): void => {
        e.stopPropagation() // Prevent any parent handlers
        
        // If Ctrl/Cmd is pressed, open in new tab
        if ((e.ctrlKey || e.metaKey) && window.api?.tabs) {
          e.preventDefault()
          window.api.tabs.create(
            `/sales-invoices/detail/${invoiceId}`,
            `Invoice #${invoiceNumber}`
          )
        } else {
          // Navigate to detail page
          const event = new CustomEvent(editSalesInvoiceEvent, {
            detail: { salesInvoice: row.original }
          })
          document.dispatchEvent(event)
        }
      }

      return (
        <button
          onClick={handleClick}
          className="font-medium text-primary hover:underline focus:outline-none focus:underline"
          title="Click to view, Ctrl/Cmd+Click to open in new tab"
        >
          {invoiceNumber}
        </button>
      )
    },
    meta: {
      label: 'Invoice Number',
      placeholder: 'Filter invoices...',
      variant: 'text',
      icon: FileText
    },
    enableColumnFilter: true
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }): JSX.Element => {
      const date = row.getValue('created_at') as string
      return <span>{formatDate(date)}</span>
    },
    meta: {
      label: 'Date',
      placeholder: 'Select date range...',
      variant: 'dateRange',
      icon: Calendar
    },
    enableColumnFilter: true
  },
  {
    id: 'party.name',
    accessorKey: 'party.name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Customer" />,
    cell: ({ row }): JSX.Element => {
      const party = row.original.party
      return <span>{party?.name || '-'}</span>
    },
    meta: {
      label: 'Customer',
      placeholder: 'Filter customers...',
      variant: 'text',
      icon: User
    },
    enableColumnFilter: true
  },
  {
    accessorKey: 'reference_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Reference" />,
    cell: ({ row }): JSX.Element => {
      const reference = row.getValue('reference_number') as string | null
      return <span>{reference || '-'}</span>
    },
    meta: {
      label: 'Reference',
      placeholder: 'Filter references...',
      variant: 'text',
      icon: FileText
    },
    enableColumnFilter: true
  },
  {
    accessorKey: 'total_amount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
    cell: ({ row }): JSX.Element => {
      const amount = row.getValue('total_amount') as number
      return <span className="font-medium text-right">{formatCurrency(amount)}</span>
    },
    meta: {
      label: 'Amount',
      placeholder: 'Filter by amount...',
      variant: 'number',
      icon: DollarSign
    },
    enableColumnFilter: true
  },
  {
    id: 'status',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }): JSX.Element => {
      const invoice = row.original
      const displayStatus = getSimplifiedSalesInvoiceStatus(invoice)

      // Define badge variant with professional gradient colors
      let variant: BadgeProps['variant'] = 'default'
      let className = 'text-xs px-2 py-0.5 font-medium border-0 shadow-sm '
      
      switch (displayStatus) {
        case 'Paid':
          className += 'bg-gradient-to-r from-success to-success/80 text-white'
          break
        case 'Partially Paid':
          className += 'bg-gradient-to-r from-warning to-warning text-white'
          break
        case 'Unpaid':
          className += 'bg-gradient-to-r from-slate-500 to-slate-600 text-white'
          break
        case 'Cancelled':
          className += 'bg-gradient-to-r from-destructive to-destructive/80 text-white'
          break
      }

      return <Badge variant={variant} className={className}>{displayStatus}</Badge>
    },
    meta: {
      label: 'Status',
      placeholder: 'Filter by status...',
      variant: 'select',
      icon: Tag,
      options: [
        { label: 'Unpaid', value: 'Unpaid' },
        { label: 'Partially Paid', value: 'Partially Paid' },
        { label: 'Paid', value: 'Paid' },
        { label: 'Cancelled', value: 'Cancelled' }
      ]
    },
    enableColumnFilter: true
  },
  {
    id: 'actions',
    cell: ({ row }): JSX.Element => {
      const salesInvoice = row.original

      const handleEdit = (e: React.MouseEvent): void => {
        // If Ctrl/Cmd is pressed, open in new tab
        if ((e.ctrlKey || e.metaKey) && window.api?.tabs) {
          e.preventDefault()
          e.stopPropagation()
          window.api.tabs.create(
            `/sales-invoices/detail/${salesInvoice.id}`,
            `Invoice #${salesInvoice.voucher_number}`
          )
        } else {
          const event = new CustomEvent(editSalesInvoiceEvent, {
            detail: { salesInvoice }
          })
          document.dispatchEvent(event)
        }
      }

      const handleDelete = (): void => {
        const event = new CustomEvent(deleteSalesInvoiceEvent, {
          detail: { salesInvoice }
        })
        document.dispatchEvent(event)
      }

      const handleMarkAsPaid = (): void => {
        const event = new CustomEvent(markAsPaidSalesInvoiceEvent, {
          detail: { salesInvoice }
        })
        document.dispatchEvent(event)
      }

      const handleDuplicate = (): void => {
        const event = new CustomEvent(duplicateSalesInvoiceEvent, {
          detail: { salesInvoice }
        })
        document.dispatchEvent(event)
      }

      const handleOpenPartyDetail = (): void => {
        if (salesInvoice.party?.id && window.api?.tabs) {
          window.api.tabs.create(
            `/party/detail/${salesInvoice.party.id}`,
            `Party: ${salesInvoice.party.name}`
          )
        }
      }

      const invoiceStatus = getSimplifiedSalesInvoiceStatus(salesInvoice)
      const canMarkAsPaid = !salesInvoice.is_cancelled && invoiceStatus !== 'Paid'

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleEdit} title="Edit (Ctrl/Cmd+Click to open in new tab)">
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDuplicate}>
              <Copy className="mr-2 h-4 w-4" />
              Duplicate
            </DropdownMenuItem>
            {salesInvoice.party?.id && (
              <DropdownMenuItem onClick={handleOpenPartyDetail}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Party Detail
              </DropdownMenuItem>
            )}
            {canMarkAsPaid && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleMarkAsPaid}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Mark as Paid
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={handleDelete}
              disabled={salesInvoice.is_cancelled}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  }
]
